import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Switch, Card, message, Space, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, LoadingOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import { useUser } from '../../contexts/userContext';
import { resellerService } from '../../services/reseller.service';
import '../../Styles/Resellers.scss';

interface FormValues {
  name: string;
  contactEmail: string;
  logoUrl: string;
  isActive: boolean;
}

const ResellerFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { state: { isAdmin } } = useUser();
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [logoPreviewError, setLogoPreviewError] = useState(false);

  const isEditing = !!id;

  useEffect(() => {
    if (isAdmin === false) {
      navigate('/');
      return;
    }
    if (isAdmin && isEditing) {
      loadReseller();
    }
  }, [isAdmin, id, navigate]);

  const loadReseller = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await resellerService.getResellerDetail(parseInt(id));
      if (response.isSuccess && response.responseObject) {
        const reseller = response.responseObject;
        form.setFieldsValue({
          name: reseller.name,
          contactEmail: reseller.contactEmail || '',
          logoUrl: reseller.logoUrl || '',
          isActive: reseller.isActive
        });
      } else {
        message.error('Failed to load reseller');
        navigate('/admin/resellers');
      }
    } catch (error) {
      message.error('Failed to load reseller');
      navigate('/admin/resellers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      if (isEditing) {
        const response = await resellerService.updateReseller(parseInt(id!), {
          name: values.name,
          contactEmail: values.contactEmail || undefined,
          logoUrl: values.logoUrl || undefined,
          isActive: values.isActive
        });
        if (response.isSuccess) {
          message.success('Reseller updated successfully');
          navigate(`/admin/resellers/${id}`);
        } else {
          message.error(response.errors?.[0] || 'Failed to update reseller');
        }
      } else {
        const response = await resellerService.createReseller({
          name: values.name,
          contactEmail: values.contactEmail || undefined,
          logoUrl: values.logoUrl || undefined
        });
        if (response.isSuccess && response.responseObject) {
          message.success('Reseller created successfully');
          const newId = response.responseObject.id ?? (response as any).id;
          if (newId) {
            navigate(`/admin/resellers/${newId}`);
          } else {
            navigate('/admin/resellers');
          }
        } else {
          message.error(response.errors?.[0] || 'Failed to create reseller');
        }
      }
    } catch (error) {
      message.error('An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const logoUrl = Form.useWatch('logoUrl', form);

  if (isAdmin === null || loading) {
    return (
      <Layout>
        <div className="reseller-form-page">
          <div className="page-loading">
            <Spin 
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              tip={isEditing ? "Loading reseller..." : "Loading..."}
            />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="reseller-form-page">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/resellers')}
          className="back-button"
        >
          Back
        </Button>

        <h1>{isEditing ? 'Edit Reseller' : 'Create New Reseller'}</h1>

        <Card className="reseller-form-card">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ isActive: true }}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[
                { required: true, message: 'Please enter the reseller name' },
                { max: 100, message: 'Name cannot exceed 100 characters' }
              ]}
            >
              <Input placeholder="B&H Photo" size="large" />
            </Form.Item>

            <Form.Item
              name="contactEmail"
              label="Contact Email"
              rules={[{ type: 'email', message: 'Please enter a valid email' }]}
            >
              <Input placeholder="partner@company.com" size="large" />
            </Form.Item>

            <Form.Item
              name="logoUrl"
              label="Logo URL"
              extra="URL to the reseller's logo for branded activation page"
              rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
            >
              <Input
                placeholder="https://example.com/logo.png"
                size="large"
                onChange={() => setLogoPreviewError(false)}
              />
            </Form.Item>

            {logoUrl && !logoPreviewError && (
              <div className="logo-preview">
                <span>Preview:</span>
                <img
                  src={logoUrl}
                  alt="Logo preview"
                  onError={() => setLogoPreviewError(true)}
                />
              </div>
            )}

            {isEditing && (
              <Form.Item
                name="isActive"
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            )}

            <Form.Item className="form-actions">
              <Space>
                <Button onClick={() => navigate('/admin/resellers')}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  icon={<SaveOutlined />}
                >
                  {isEditing ? 'Update Reseller' : 'Create Reseller'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </Layout>
  );
};

export default ResellerFormPage;
